/**
 * VideoCallProvider - Manages Chime SDK meeting session and state
 * Wraps components that need access to video calling functionality
 *
 * A call is displayed on the in-app CallStage (a full-viewport overlay in THIS document),
 * not in a popup window, and answering asks for real full screen on the click that answers.
 * The reasoning for the move - full screen and screen sharing both need transient activation
 * that a popup document does not have - is written up at the top of CallStage.jsx.
 *
 * VideoCallWindow is still imported for its teardown: closeVideoCallWindow is a no-op when no
 * popup is open, and leaving it in the teardown paths means a window left over from an older
 * session is still cleaned up rather than outliving the call.
 *
 * ONE MEETING SESSION FOR THE WHOLE BROWSER SESSION
 * -------------------------------------------------
 * Moving the call in-app changed the meeting lifecycle from per-call to per-session, which is
 * worth stating outright because nothing on screen says so. The popup rendered
 * VideoCallContent in a separate React root with its OWN <MeetingProvider>, so every call got
 * a fresh MeetingManager and the previous one died with the window. The MeetingProvider at the
 * bottom of this file is mounted once, at the app root, for as long as the tab is open - so
 * every call in that tab now shares one MeetingManager, and a second call is a path that did
 * not previously exist.
 *
 * Two things make consecutive calls clean rather than merely usual:
 *  - the meeting session is owned by a callId and joins/leaves are serialised (callSession.js,
 *    which explains what MeetingManager.leave does and does not reset); and
 *  - CallStage is keyed on the callId, so call N+1 gets a fresh VideoCallContent instead of
 *    call N's, complete with its isJoined, duration, raised hands and error (see the render).
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MeetingProvider,
    useMeetingManager,
    useMeetingStatus,
    useLocalVideo,
    useToggleLocalMute,
    useAudioVideo,
    useContentShareState,
    useContentShareControls,
    MeetingStatus
} from 'amazon-chime-sdk-component-library-react';
import { ThemeProvider } from 'styled-components';
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js';
import { closeVideoCallWindow } from './VideoCallWindow';
import CallStage from './CallStage';
import { requestCallFullscreen, exitCallFullscreen } from './useCallFullscreen';
import { endCallSession } from './callSession';
import { useChimeTheme } from './useCallTheme';
import { authHeaders } from '../../utils/authHeaders';

// API base URL - use relative path to leverage Vite proxy
// This works in both localhost and network access scenarios
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// How long the caller rings before the call is treated as unanswered
const UNANSWERED_CALL_TIMEOUT_MS = 30000;
// How long the callee rings before the incoming call is treated as gone. Always longer than
// the caller's timeout so the caller's own 'call-missed' wins the race when signalling works.
const INCOMING_RING_TIMEOUT_MS = 45000;

// Create context for video call state
const VideoCallContext = createContext(null);

// Hook to access video call context
export const useVideoCall = () => {
    const context = useContext(VideoCallContext);
    if (!context) {
        throw new Error('useVideoCall must be used within a VideoCallProvider');
    }
    return context;
};

/**
 * Ask for the camera/microphone up front, from the window the engineer is looking at and
 * while their click still counts as user activation.
 *
 * The call UI runs in this window's realm even though it is displayed in the popup, so the
 * Chime SDK would otherwise raise the permission prompt in the tab hidden behind the popup -
 * and it swallows a denial, leaving a silent, cameraless call with no error anywhere.
 */
async function requestCallMedia(callType) {
    const wantsVideo = callType === 'video';
    let stream;

    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: wantsVideo });
    } catch (error) {
        // A refusal is fatal; a missing or busy camera is not - audio alone is still a call
        if (wantsVideo && error.name !== 'NotAllowedError' && error.name !== 'SecurityError') {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
            throw error;
        }
    }

    stream.getTracks().forEach(track => track.stop());
}

/**
 * Names the media failure the engineer actually hit.
 *
 * Every preflight failure used to be reported as "access was blocked", which sends them to
 * the browser's permission dialog when the real cause is a machine with no microphone, or a
 * camera another application is still holding.
 */
function mediaErrorMessage(error, t) {
    switch (error && error.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return t('aiChat.calls.mediaAccessDenied');
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return t('aiChat.calls.mediaDeviceNotFound');
        case 'NotReadableError':
        case 'TrackStartError':
        case 'AbortError':
            return t('aiChat.calls.mediaDeviceBusy');
        default:
            return t('aiChat.calls.mediaAccessFailed', {
                reason: (error && (error.message || error.name)) || t('aiChat.messages.unknown')
            });
    }
}

// Inner provider that has access to meeting manager
function VideoCallInnerProvider({ children, socket, userId, userName }) {
    const { t } = useTranslation();
    const meetingManager = useMeetingManager();
    const meetingStatus = useMeetingStatus();
    const { toggleVideo, isVideoEnabled } = useLocalVideo();
    const { muted, toggleMute } = useToggleLocalMute();
    const audioVideo = useAudioVideo();
    const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
    const { toggleContentShare } = useContentShareControls();

    // Call state
    const [callState, setCallState] = useState({
        isInCall: false,
        isRinging: false,
        isIncoming: false,
        callId: null,
        sessionId: null,
        callType: 'video', // 'video' or 'audio'
        participants: [],
        initiatorUserId: null,
        initiatorName: null,
        error: null
    });

    // Refs for cleanup and state tracking
    const callTimeoutRef = useRef(null);
    const isInCallRef = useRef(false);
    const joinCallRef = useRef(null);
    const handleCallMissedRef = useRef(null);
    // Live call state for callbacks that outlive the render that created them (the
    // unanswered-call timeout below, and the socket reconnect handler)
    const callStateRef = useRef(callState);
    callStateRef.current = callState;

    // The props the CallStage is showing, or null when there is no call on screen. This is
    // the whole of the "where does the call render" question now that there is no popup.
    const [stage, setStage] = useState(null);

    /**
     * Take the call off the screen.
     *
     * Every teardown path goes through here, so full screen is always given back: a browser
     * left in full screen with no call in it is a window the engineer cannot get out of
     * except by knowing about Escape. closeVideoCallWindow is kept for a popup left over from
     * a session that started before this build.
     */
    const closeCallSurface = useCallback(() => {
        setStage(null);
        exitCallFullscreen();
        closeVideoCallWindow();
    }, []);

    // Keep ref in sync with state
    useEffect(() => {
        isInCallRef.current = callState.isInCall;
    }, [callState.isInCall]);

    // Start a new call - shown on the in-app CallStage, full screen if the browser allows
    const startCall = useCallback(async (sessionId, callType = 'video') => {
        // Ask for full screen HERE, before the first await in this function, because this
        // still runs inside the click that started the call and that is the only moment the
        // browser will consider the request. Everything below - the camera/microphone prompt
        // above all, which the engineer can leave sitting open - outlasts the activation.
        // Refusal is not a failure: the CallStage is a full-viewport overlay either way.
        requestCallFullscreen();

        // Set once the call is on screen, so the failure paths below know whether taking the
        // surface away would strand the engineer in a call with no way to end it.
        let stageShown = false;

        try {
            // navigator.mediaDevices requires a secure context (HTTPS or localhost)
            if (!navigator.mediaDevices) {
                throw new Error(t('aiChat.calls.secureConnectionRequired'));
            }

            // This socket is the only thing that rings the callee ('call-initiate' below).
            // Without it the meeting would be created and the window opened for a call that
            // can never reach anybody, and the emit would silently no-op. The provider now
            // sits at the app root and takes the socket from PresenceContext, so it can
            // genuinely be missing for a moment after load - fail before anything is created.
            if (!socket) {
                throw new Error(t('aiChat.calls.notConnected'));
            }

            // Get the devices before creating anything, so a denial fails the call visibly
            // instead of producing a silent one. The prompt is raised by this window, which
            // is the window the engineer is looking at - there is no longer a popup in front
            // of it to hide the prompt behind.
            try {
                await requestCallMedia(callType);
            } catch (mediaError) {
                console.error('[VideoCall] Camera/microphone access failed:', mediaError);
                throw new Error(mediaErrorMessage(mediaError, t));
            }

            setCallState(prev => ({
                ...prev,
                isRinging: true,
                callType,
                sessionId,
                // Start the ring with an empty roster. The socket-driven teardowns
                // (ended/rejected/missed) do not go through resetCallState, so whoever
                // answered the previous call is still listed here - and the unanswered-call
                // timeout below would read that and never fire again.
                participants: [],
                error: null
            }));

            // Call backend to create meeting
            const response = await fetch(`${API_BASE_URL}/api/ai/video-call/start`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ sessionId, callType, userId })
            });

            if (!response.ok) {
                const errorData = await response.json();
                // If there's already an active call in this session, auto-join it
                if (response.status === 409 && errorData.callId) {
                    console.log('[VideoCall] Active call exists, joining instead:', errorData.callId);
                    setCallState(prev => ({ ...prev, isRinging: false }));
                    // joinCall puts the call on the stage itself. Its own full-screen request
                    // is a no-op by now (the activation from the click is long spent), which
                    // is exactly why the one at the top of this function is unconditional.
                    return await joinCallRef.current(errorData.callId, sessionId, callType);
                }
                throw new Error(errorData.error || t('aiChat.calls.failedToStart'));
            }

            const data = await response.json();
            const { call } = data;

            // Update state and ref immediately to prevent race conditions
            isInCallRef.current = true;
            console.log('[VideoCall] startCall: Set isInCallRef.current = true for userId:', userId, 'callId:', call.id);
            setCallState(prev => ({
                ...prev,
                isInCall: true,
                isRinging: true,
                callId: call.id,
                initiatorUserId: userId,
                initiatorName: userName
            }));

            // Put the call on screen
            setStage({
                callId: call.id,
                callType,
                sessionId,
                userId,
                userName,
                meetingData: call.meeting,
                attendeeData: call.attendee,
                socket,
                onEndCall: () => {
                    closeCallSurface();
                    resetCallState();
                },
                initiatorName: userName
            });

            // The stage now carries the only end-call control there is, so the catch below
            // must not take it away while isInCall is set - that would strand the engineer in
            // a call they can neither see nor hang up.
            stageShown = true;

            // Notify other participants via socket. Guarded at the top of startCall, so this
            // is never the silent no-op it used to be when the socket had not arrived yet.
            socket.emit('call-initiate', {
                callId: call.id,
                sessionId,
                callType,
                initiatorUserId: userId,
                initiatorName: userName
            });

            // Set timeout for unanswered call (30 seconds).
            // Read the live state through the ref: this callback outlives the render that
            // built it, where isRinging was still false, so the guard was never true.
            callTimeoutRef.current = setTimeout(() => {
                const { isRinging, participants } = callStateRef.current;
                if (isRinging && !participants.length) {
                    handleCallMissedRef.current(call.id, sessionId);
                }
            }, UNANSWERED_CALL_TIMEOUT_MS);

            return call;
        } catch (error) {
            console.error('[VideoCall] Error starting call:', error);
            // Full screen was asked for on the click, before anything could fail, so give it
            // back here: a browser sitting in full screen showing the app with no call in it
            // is worse than the error itself. The stage is only taken away while it is not
            // yet the engineer's only way out of a call in progress.
            if (!stageShown) {
                closeCallSurface();
            }
            setCallState(prev => ({
                ...prev,
                isRinging: false,
                error: error.message
            }));
            throw error;
        }
    }, [socket, userId, userName, t]);

    // Answer an incoming call - goes full screen on this click (see the note inside)
    const joinCall = useCallback(async (callId, sessionId, callType = callState.callType) => {
        // THE full-screen request. This is what the engineer asked for - the call goes full
        // screen when they answer - and this line is the only place it can happen: it runs
        // inside the Join click, before this function's first await, while the click still
        // counts as user activation. It is deliberately the very first statement; anything
        // awaitable put above it silently turns full screen off, with no error anywhere.
        requestCallFullscreen();

        // Same as startCall: whether taking the surface away would strand the engineer.
        let stageShown = false;

        try {
            // Stop the incoming ring the moment Accept is pressed. Clearing it only after the
            // camera/microphone prompt - which the engineer can leave sitting open - let the
            // 45s incoming-ring timeout fire in the middle of the join and paint a permanent
            // 'Missed call from ...' banner over a call that then connected fine.
            setCallState(prev => ({
                ...prev,
                isIncoming: false,
                isRinging: false,
                error: null
            }));

            // 'call-accept' below is what stops the caller ringing out. Without a socket they
            // give up after 30s and end the meeting we are about to join, so fail visibly
            // here rather than joining a call that is about to be torn down underneath us.
            if (!socket) {
                throw new Error(t('aiChat.calls.notConnected'));
            }

            // Same reason as startCall: get the devices from this window, on this click
            try {
                await requestCallMedia(callType);
            } catch (mediaError) {
                console.error('[VideoCall] Camera/microphone access failed:', mediaError);
                throw new Error(mediaErrorMessage(mediaError, t));
            }

            // Call backend to join meeting
            const response = await fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/join`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ userId })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || t('aiChat.calls.failedToJoin'));
            }

            const data = await response.json();
            const { call } = data;

            // Update state
            setCallState(prev => ({
                ...prev,
                isInCall: true,
                isRinging: false,
                isIncoming: false,
                callId: call.id,
                sessionId: call.sessionId,
                callType: call.callType
            }));

            // Put the answered call on screen
            setStage({
                callId: call.id,
                callType: call.callType,
                sessionId: call.sessionId,
                userId,
                userName,
                meetingData: call.meeting,
                attendeeData: call.attendee,
                socket,
                onEndCall: () => {
                    closeCallSurface();
                    resetCallState();
                },
                initiatorName: callState.initiatorName
            });

            // Same as startCall: the stage now carries the only way out of the call, so a
            // later failure must leave it alone.
            stageShown = true;

            // Notify via socket. Guarded at the top of joinCall, so a missing socket is
            // reported instead of silently skipping the accept and ringing the caller out.
            socket.emit('call-accept', {
                callId,
                sessionId,
                userId,
                userName
            });

            return call;
        } catch (error) {
            console.error('[VideoCall] Error joining call:', error);
            // Answering asked for full screen before the media prompt and the join, so a
            // failure in either has to hand it back - otherwise the browser sits full screen
            // on the app with the failure reported in a banner the engineer cannot see past.
            if (!stageShown) {
                closeCallSurface();
            }
            setCallState(prev => ({
                ...prev,
                isIncoming: false,
                error: error.message
            }));
            throw error;
        }
    }, [socket, userId, userName, callState.initiatorName, callState.callType, t]);

    // Keep joinCall ref in sync for use by startCall's 409 handler
    joinCallRef.current = joinCall;

    // Reject an incoming call
    const rejectCall = useCallback(async (callId, sessionId) => {
        // Stop the ring before anything that can block. Clearing the state only after the
        // reject POST left the modal ringing for the whole round trip after Decline was
        // pressed - and for ever if the request hung, with the ringtone still going and no
        // way out. sessionId goes with it: left set, this tab still counts as being in the
        // signalling room of a call it has just declined.
        setCallState(prev => ({
            ...prev,
            isIncoming: false,
            callId: null,
            sessionId: null
        }));

        // Notify via socket. This, not the POST, is what stops the caller ringing out, so it
        // goes first; the server's call-reject handler only broadcasts and never reads the
        // call row, so it does not depend on the POST below having landed.
        if (socket) {
            socket.emit('call-reject', {
                callId,
                sessionId,
                userId,
                userName
            });
        }

        try {
            // Call backend to reject
            const response = await fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/reject`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ userId })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            // The socket emit above has already stopped the caller ringing, so this looks
            // like a clean decline - but this POST is the only thing that sets the call to
            // 'rejected' and ends the Chime meeting. Lost, the row stays 'ringing' and 409s
            // the next call in this session, which the engineer meets later as a call that
            // simply will not start. Console-only, that is invisible; report it.
            console.error('[VideoCall] Error rejecting call:', error);
            setCallState(prev => ({ ...prev, error: t('aiChat.calls.declineNotRecorded') }));
        }
    }, [socket, userId, userName, t]);

    // Reset call state
    const resetCallState = useCallback(() => {
        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }
        isInCallRef.current = false;
        setCallState(prev => ({
            isInCall: false,
            isRinging: false,
            isIncoming: false,
            callId: null,
            sessionId: null,
            callType: 'video',
            participants: [],
            initiatorUserId: null,
            initiatorName: null,
            // Clearing the call is not the same as clearing the report of why it failed.
            // Closing the call window by hand is the only way out of a call a refused signal
            // has stranded, and every one of those exits lands here - so this used to erase
            // the single on-screen report of the refusal, leaving the engineer with a call
            // that simply vanished. The banner has its own dismiss (clearError) and the next
            // call clears it at the ring, so keeping it costs nothing.
            error: prev.error
        }));
    }, []);

    // End the current call
    const endCall = useCallback(async () => {
        try {
            const { callId, sessionId } = callState;

            if (callTimeoutRef.current) {
                clearTimeout(callTimeoutRef.current);
                callTimeoutRef.current = null;
            }

            // Take the call off the screen and hand full screen back
            closeCallSurface();

            // Leave the meeting (may not be in the meeting at all if the join failed).
            // Released by callId, not unconditionally: there is one MeetingManager for the
            // whole session now, so an unconditional leave here is a leave of whatever
            // session happens to be up - which, once calls can follow one another, is not
            // necessarily this one. A no-op when the stage's own teardown got there first.
            await endCallSession(meetingManager, callId);

            if (callId) {
                // Call backend to end
                await fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/end`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ userId })
                });

                // Notify via socket
                if (socket) {
                    socket.emit('call-end', {
                        callId,
                        sessionId,
                        userId,
                        userName
                    });
                }
            }

            // Reset state
            resetCallState();
        } catch (error) {
            console.error('[VideoCall] Error ending call:', error);
        }
    }, [meetingManager, callState, socket, userId, userName, resetCallState]);

    // Leave call (without ending it for others)
    const leaveCall = useCallback(async () => {
        try {
            const { callId, sessionId } = callState;

            // Take the call off the screen and hand full screen back
            closeCallSurface();

            // By callId - same reasoning as endCall above.
            await endCallSession(meetingManager, callId);

            if (callId) {
                await fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/leave`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ userId })
                });

                if (socket) {
                    socket.emit('call-participant-left', {
                        callId,
                        sessionId,
                        userId,
                        userName
                    });
                }
            }

            // Reset state
            resetCallState();
        } catch (error) {
            console.error('[VideoCall] Error leaving call:', error);
        }
    }, [meetingManager, callState, socket, userId, userName, resetCallState]);

    // Handle missed call
    const handleCallMissed = useCallback(async (callId, sessionId) => {
        // Take the stage down first: it is showing 'Calling...' and would otherwise keep the
        // engineer waiting, full screen, on a call that is over
        closeCallSurface();
        isInCallRef.current = false;
        setCallState(prev => ({
            ...prev,
            isInCall: false,
            isRinging: false,
            callId: null,
            error: t('aiChat.calls.callNotAnswered')
        }));

        try {
            await fetch(`${API_BASE_URL}/api/ai/video-call/${callId}/end`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ userId })
            });

            if (socket) {
                socket.emit('call-missed', {
                    callId,
                    sessionId,
                    initiatorUserId: userId
                });
            }
        } catch (error) {
            console.error('[VideoCall] Error handling missed call:', error);
        }
    }, [socket, userId, t]);

    // Keep the ref in sync for the unanswered-call timeout
    handleCallMissedRef.current = handleCallMissed;

    // Toggle video
    const toggleVideoEnabled = useCallback(() => {
        toggleVideo();

        if (socket && callState.callId) {
            socket.emit('call-media-update', {
                callId: callState.callId,
                sessionId: callState.sessionId,
                userId,
                videoEnabled: !isVideoEnabled,
                audioEnabled: !muted
            });
        }
    }, [toggleVideo, socket, callState, userId, isVideoEnabled, muted]);

    // Toggle audio
    const toggleAudioEnabled = useCallback(() => {
        toggleMute();

        if (socket && callState.callId) {
            socket.emit('call-media-update', {
                callId: callState.callId,
                sessionId: callState.sessionId,
                userId,
                videoEnabled: isVideoEnabled,
                audioEnabled: muted // Will be toggled
            });
        }
    }, [toggleMute, socket, callState, userId, isVideoEnabled, muted]);

    // Toggle screen share
    const toggleScreenShare = useCallback(async () => {
        try {
            await toggleContentShare();
        } catch (error) {
            console.error('[VideoCall] Error toggling screen share:', error);
            setCallState(prev => ({ ...prev, error: t('aiChat.calls.screenShareFailed') }));
        }
    }, [toggleContentShare, t]);

    // Handle incoming call from socket
    const handleIncomingCall = useCallback((data) => {
        const { callId, sessionId, callType, initiatorUserId: callerId, initiatorName: callerName } = data;

        console.log('[VideoCall] Incoming call event:', {
            callerId,
            userId,
            isInCallRef: isInCallRef.current,
            incomingCallId: callId
        });

        // Don't show incoming call if we're the initiator (use String for type-safe comparison)
        // Also don't show if we're already in a call (use ref to avoid stale closure)
        if (String(callerId) === String(userId)) {
            console.log('[VideoCall] Ignoring incoming call - we are the initiator');
            return;
        }
        if (isInCallRef.current) {
            console.log('[VideoCall] Ignoring incoming call - already in a call');
            return;
        }

        console.log('[VideoCall] Showing incoming call notification');
        setCallState(prev => ({
            ...prev,
            isIncoming: true,
            callId,
            sessionId,
            callType,
            initiatorUserId: callerId,
            initiatorName: callerName
        }));
    }, [userId]);

    // Handle call ended from socket
    const handleCallEnded = useCallback(async (data) => {
        const { callId, endedBy } = data;

        // Only process if it's for our current call
        if (callState.callId !== callId) return;

        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }

        // Take the call off the screen: it has no idea the call is over and would keep
        // showing the grid, full screen, with the duration counting up
        closeCallSurface();

        // The callId carried by the event, not "whatever is up": these handlers can be
        // delivered late (a re-emit, or a room re-join after a reconnect), and there is now a
        // single MeetingManager for the session for a late one to reach into.
        await endCallSession(meetingManager, callId);

        isInCallRef.current = false;
        setCallState(prev => {
            // call-end fans out to `user-chat-<sessionId>` AND to every participant's
            // `user-<id>` room, and a callee who is being rung is in both - so this arrives
            // twice on the same socket. Both copies pass the guard above (it reads the
            // callId captured at bind time), and when they land in one React batch the
            // second one overwrites the first: a caller who hung up mid-ring was reported
            // as "ended the call" instead of a missed call. Re-check against the live
            // previous state so the duplicate is a no-op.
            if (prev.callId !== callId) return prev;

            return {
                ...prev,
                isInCall: false,
                isRinging: false,
                isIncoming: false,
                callId: null,
                // A call that was still ringing here was never answered - say that, not "ended"
                error: endedBy === userId
                    ? null
                    : (prev.isIncoming
                        ? t('aiChat.calls.missedCallFrom', { name: prev.initiatorName || t('aiChat.messages.unknown') })
                        : t('aiChat.calls.callEndedByOther'))
            };
        });
    }, [callState.callId, meetingManager, userId, t]);

    // Handle call rejected from socket
    const handleCallRejected = useCallback(async (data) => {
        const { callId, userId: rejectedBy } = data;

        if (callState.callId !== callId) return;

        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }

        closeCallSurface();

        // The callId carried by the event, not "whatever is up": these handlers can be
        // delivered late (a re-emit, or a room re-join after a reconnect), and there is now a
        // single MeetingManager for the session for a late one to reach into.
        await endCallSession(meetingManager, callId);

        isInCallRef.current = false;
        setCallState(prev => {
            // Same hardening as handleCallEnded: the guard above reads the callId captured
            // when this handler was bound, so every copy of the event passes it. One room
            // broadcast is one delivery today, but a second delivery (a re-emit, or this
            // socket sitting in the room twice after a reconnect) would land on a call this
            // has already cleared and re-report it. Re-check the live state instead.
            if (prev.callId !== callId) return prev;

            return {
                ...prev,
                isInCall: false,
                isRinging: false,
                // The decline is broadcast to the whole chat room, so the engineer's other
                // open tabs are still ringing for a call that is now over - stop them too.
                // When we are the one who declined (from another tab), that is not a failure
                // to report.
                isIncoming: false,
                callId: null,
                error: String(rejectedBy) === String(userId) ? null : t('aiChat.calls.callDeclined')
            };
        });
    }, [callState.callId, meetingManager, userId, t]);

    // Handle call accepted from socket
    const handleCallAccepted = useCallback((data) => {
        const { userId: acceptedBy } = data;

        // Clear the timeout since call was answered
        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }

        setCallState(prev => {
            // The accept is broadcast to the whole chat room, and with the provider mounted
            // at the app root every tab this engineer has open is in that room and ringing.
            // When we are the one who answered (in another tab), stop this tab's ring instead
            // of listing ourselves as a remote participant - otherwise it rings for the full
            // 45s and then claims a missed call we actually took.
            if (String(acceptedBy) === String(userId)) {
                return { ...prev, isIncoming: false, isRinging: false };
            }
            return {
                ...prev,
                isRinging: false,
                participants: [...prev.participants, acceptedBy]
            };
        });
    }, [userId]);

    // Handle the caller giving up before we answered (their 30s timeout, or they hung up).
    // Without this the incoming modal rings on forever with no way to know the call is gone.
    const handleCallCancelled = useCallback((data) => {
        const { callId, userId: leftUserId, initiatorUserId: missedByUserId } = data || {};

        setCallState(prev => {
            if (prev.callId !== callId || !prev.isIncoming) return prev;

            // 'call-participant-left' is broadcast to the whole chat room, so in a call with
            // three or more people it also reaches somebody who is still being rung: A and B
            // are in the call, A invites C, B leaves - and C's ring must not be cancelled
            // with 'Missed call from A' while A is still there. Only the person who is
            // calling us walking away ends our ring.
            const whoLeft = leftUserId != null ? leftUserId : missedByUserId;
            if (whoLeft != null && prev.initiatorUserId != null &&
                String(whoLeft) !== String(prev.initiatorUserId)) {
                return prev;
            }

            return {
                ...prev,
                isIncoming: false,
                callId: null,
                error: t('aiChat.calls.missedCallFrom', { name: prev.initiatorName || t('aiChat.messages.unknown') })
            };
        });
    }, [t]);

    // The chat service refuses any call-* signal it cannot attribute to a live participant of
    // the chat (unauthenticated socket, database down, not a participant) and reports it on
    // 'call-error'. Every call-* emit in this file is fire-and-forget with no acknowledgement
    // callback, so without this listener a refused call is completely silent: the caller sits
    // on 'Calling...' while the callee is never rung.
    const handleCallError = useCallback(async (data) => {
        const { event, callId: erroredCallId, sessionId: erroredSessionId, error: reason } = data || {};
        console.error('[VideoCall] Call signal refused by the chat service:', data);

        // A refused invite is reported by the invite modal, from the acknowledgement the
        // server sends alongside this event (failCallEvent does both). That report is the
        // better one - it names the people who were not rung and says when they are simply
        // offline - and it appears in the call window the engineer clicked Invite in, whereas
        // this banner renders in the window behind it. One failure, one report: leave it to
        // the modal. An invite never touches the call in progress, so there is nothing else
        // to do with it here.
        if (event === 'call-invite-user') {
            return;
        }

        const message = t('aiChat.calls.callSignalRefused', {
            event: event || t('aiChat.messages.unknown'),
            reason: reason || t('aiChat.messages.unknown')
        });

        // Read the live call through the ref, the way the unanswered-call timeout does: the
        // teardown below has to happen outside the state updater, so it needs the callId.
        const live = callStateRef.current;

        // The refusal carries the callId of the signal that was refused, when the signal
        // carried one at all; fall back to the session when it did not. A refusal naming some
        // other call is still shown, but must not touch the call in progress.
        const isCurrentCall = erroredCallId
            ? String(erroredCallId) === String(live.callId)
            : (!erroredSessionId || String(erroredSessionId) === String(live.sessionId));

        // A call that is up is NOT torn down here. The Chime media plane does not run over
        // this socket, so a refused hand-raise or camera toggle must never drop a call people
        // are talking on - it is reported and nothing else. (The call window binds
        // 'call-error' too, so that report is visible in the window in front of them.)
        if (!isCurrentCall || (!live.isRinging && !live.isIncoming)) {
            setCallState(prev => ({ ...prev, error: message }));
            return;
        }

        // A call that was still being set up is over: refused, it will never ring or be
        // answered. Tear it down the way a missed call is torn down, but keep the message -
        // it is the only report of the refusal there is.
        if (callTimeoutRef.current) {
            clearTimeout(callTimeoutRef.current);
            callTimeoutRef.current = null;
        }

        // The stage is the only call UI there is, and it is showing 'Calling...'. Leaving it
        // up strands the caller for ever, full screen, over the banner that explains why.
        closeCallSurface();

        isInCallRef.current = false;
        setCallState(prev => ({
            ...prev,
            isInCall: false,
            isRinging: false,
            isIncoming: false,
            callId: null,
            error: message
        }));

        // Release the call row. Nothing else will: the 30s unanswered-call teardown is
        // guarded on isRinging, which is now false, so the row would sit at 'ringing' and the
        // next call in this session would 409 on it and auto-join a meeting nobody is in.
        // Only the initiator can end it - a callee whose signal was refused never joined, so
        // the backend would (rightly) refuse them with a 403.
        if (live.callId && String(live.initiatorUserId) === String(userId)) {
            try {
                await fetch(`${API_BASE_URL}/api/ai/video-call/${live.callId}/end`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ userId })
                });
            } catch (endError) {
                console.error('[VideoCall] Error ending call after a refused signal:', endError);
            }
        }
    }, [userId, t]);

    // Socket event listeners
    useEffect(() => {
        if (!socket) return;

        socket.on('call-error', handleCallError);
        socket.on('call-incoming', handleIncomingCall);
        socket.on('call-ended', handleCallEnded);
        socket.on('call-rejected', handleCallRejected);
        socket.on('call-accepted', handleCallAccepted);
        socket.on('call-missed', handleCallCancelled);
        socket.on('call-participant-left', handleCallCancelled);

        return () => {
            socket.off('call-error', handleCallError);
            socket.off('call-incoming', handleIncomingCall);
            socket.off('call-ended', handleCallEnded);
            socket.off('call-rejected', handleCallRejected);
            socket.off('call-accepted', handleCallAccepted);
            socket.off('call-missed', handleCallCancelled);
            socket.off('call-participant-left', handleCallCancelled);
        };
    }, [socket, handleCallError, handleIncomingCall, handleCallEnded, handleCallRejected, handleCallAccepted, handleCallCancelled]);

    // Stay in the call's signalling room.
    // accept / decline / hand-raise / participant-left / missed are all broadcast to
    // `user-chat-<sessionId>`, and Socket.IO drops room membership on every reconnect while
    // keeping the same client object - so the effect that joined the room (in UserChatTab)
    // never re-runs and the call goes deaf. Re-join here for as long as a call is live.
    useEffect(() => {
        if (!socket) return;

        const joinCallRoom = () => {
            const { sessionId, isInCall, isIncoming, isRinging } = callStateRef.current;
            if (!sessionId) return;
            if (!isInCall && !isIncoming && !isRinging) return;
            socket.emit('join-user-chat', { sessionId, userId });
        };

        joinCallRoom();
        socket.on('connect', joinCallRoom);
        return () => socket.off('connect', joinCallRoom);
    }, [socket, userId, callState.sessionId, callState.isInCall, callState.isIncoming, callState.isRinging]);

    // Stop an incoming call ringing forever when the caller gives up without telling us
    // (browser crash, or the signalling room was lost on a reconnect)
    useEffect(() => {
        if (!callState.isIncoming) return;

        const timer = setTimeout(() => {
            setCallState(prev => {
                if (!prev.isIncoming) return prev;
                return {
                    ...prev,
                    isIncoming: false,
                    callId: null,
                    error: t('aiChat.calls.missedCallFrom', { name: prev.initiatorName || t('aiChat.messages.unknown') })
                };
            });
        }, INCOMING_RING_TIMEOUT_MS);

        return () => clearTimeout(timer);
    }, [callState.isIncoming, callState.callId, t]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (callTimeoutRef.current) {
                clearTimeout(callTimeoutRef.current);
            }
        };
    }, []);

    // Never leave the browser in full screen with no call in it. The stage is torn down by
    // several paths that do not all run closeCallSurface (a socket teardown racing an unmount,
    // the provider itself going away on logout), and the engineer would be left in a
    // full-screen app with no obvious way back.
    //
    // Only ever fires for a stage that WAS there and has gone: this provider mounts at the app
    // root for the whole session, so an unconditional exit here would also cancel full screen
    // that has nothing to do with a call (the floor-plan and visualisation views use it too).
    const hadStageRef = useRef(false);
    useEffect(() => {
        if (stage) {
            hadStageRef.current = true;
            return;
        }
        if (!hadStageRef.current) return;
        hadStageRef.current = false;
        exitCallFullscreen();
    }, [stage]);

    const value = {
        // State
        ...callState,
        meetingStatus,
        isVideoEnabled,
        isAudioEnabled: !muted,
        isScreenSharing: isLocalUserSharing,
        isSomeoneSharing: !!sharingAttendeeId,
        sharingAttendeeId,

        // Identity, so a component mounted without props can still invite and signal as the
        // current user rather than as nobody
        userId,
        userName,
        socket,

        // Actions
        startCall,
        joinCall,
        rejectCall,
        endCall,
        leaveCall,
        toggleVideoEnabled,
        toggleAudioEnabled,
        toggleScreenShare,

        // Clear error
        clearError: () => setCallState(prev => ({ ...prev, error: null }))
    };

    return (
        <VideoCallContext.Provider value={value}>
            {children}
            {/* The call itself. Rendered after the app so it sits over it, and only while
                there is a call: no call, no overlay, no stray Chime session.

                Keyed on the call. Without the key, a stage that goes straight from call A to
                call B is a props change on the SAME VideoCallContent, which keeps A's state:
                `isJoined` is still true, so its join effect returns early and B never joins
                the meeting at all, and A's duration, raised hands and error banner carry over
                on top. This is not hypothetical: it is the same defect the popup already hit
                and fixed by refusing to recycle a window from an earlier call - see the note
                at VideoCallWindow.openVideoCallWindow, which names "isJoined already true"
                exactly. The popup's fix was to throw the React root away; in-app there is no
                window to throw away, so the key does it instead. */}
            {stage && <CallStage key={stage.callId} {...stage} />}
        </VideoCallContext.Provider>
    );
}

// Main provider component
export function VideoCallProvider({ children, socket, userId, userName }) {
    const chimeTheme = useChimeTheme();

    return (
        <ThemeProvider theme={chimeTheme}>
            <MeetingProvider>
                <VideoCallInnerProvider
                    socket={socket}
                    userId={userId}
                    userName={userName}
                >
                    {children}
                </VideoCallInnerProvider>
            </MeetingProvider>
        </ThemeProvider>
    );
}

export default VideoCallProvider;
